import {
  Accordion,
  AccordionItem,
  Box,
  Flex,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MotionAccordionPanel, Select } from '@time-line/components/common';
import { Refresh } from '@time-line/components/icons';
import { isSubscription } from 'components/cart/utils/typeInference';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { DiscountClass } from 'types/cart/PromoCode';
import { PurchaseType } from 'types/product/PurchaseType';
import type { Subscription } from 'types/product/Subscription';
import type { Variant } from 'types/product/Variant';
import type { VariantOrSubscription } from 'types/product/VariantOrSubscription';

import type { BaseConfiguratorProps } from '.';
import { ProductConfiguratorCancellationNote } from './ui/ProductConfiguratorCancellationNote';
import { SingleVariantAccordionItem } from './ui/SingleVariantAccordionItem';
import { Wrapper } from './Wrapper';

interface SubscriptionSingleVariantProps extends BaseConfiguratorProps {
  availableForSale: boolean;
  otpVariant: Variant;
  variant: Variant;
  subscriptions: Subscription[];
}

export function SubscriptionSingleVariant({
  variant,
  subscriptions,
  availableForSale,
  showSavingsAsAmount,
  isDiscountHidden,
  promoCodes,
  purchaseType,
  otpVariant,
  ...props
}: SubscriptionSingleVariantProps) {
  const availableSubscriptions = subscriptions?.length
    ? subscriptions.filter((item) => item.availableForSale)
    : [];

  const hasMoreThanOneSub = subscriptions.length > 1;
  const showAll = !purchaseType;
  const showSubOnly = purchaseType === PurchaseType.SUB;
  const isOptionsClickable = showAll;
  const defaultSubscription = availableSubscriptions[0];
  const discountCodes =
    promoCodes
      ?.filter(({ discountClass }) => discountClass === DiscountClass.PRODUCT)
      .map(({ code }) => code) || [];

  const initialSelected =
    props.selectedVariant ||
    (showSubOnly ? defaultSubscription : variant || defaultSubscription);

  const [selectedVariant, setSelectedVariant] =
    useState<VariantOrSubscription>(initialSelected);

  const [accordionIndex, setAccordionIndex] = useState(0);

  const handleSelect = (item: VariantOrSubscription) => {
    setSelectedVariant(item);
    props.setSelectedVariant?.(item);
  };

  const onAccordionChange = (index: number) => {
    setAccordionIndex(index);

    if (showSubOnly && index === 0) {
      handleSelect(defaultSubscription);
      return;
    }

    if (index === 0) {
      handleSelect(variant);
    }
    if (index === 1) {
      handleSelect(defaultSubscription);
    }
  };

  const onSelectChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const val = event.target.value;
    const selectedSub = subscriptions.find(
      (item) => item.sellingPlan.id === val,
    );
    if (!selectedSub) return;

    handleSelect(selectedSub);
  };

  useEffect(() => {
    setSelectedVariant(initialSelected);

    const nextIndex = !showSubOnly && isSubscription(initialSelected) ? 1 : 0;

    setAccordionIndex(nextIndex);
  }, [initialSelected, showSubOnly]);

  return (
    <Wrapper
      className="product_subscriptionSingleVariant"
      variant={selectedVariant}
      otpVariant={otpVariant}
      availableForSale={availableForSale}
      isDiscountHidden={isDiscountHidden}
      purchaseType={
        isSubscription(selectedVariant) ? PurchaseType.SUB : PurchaseType.OTP
      }
      promoCodes={promoCodes}
      {...props}
    >
      <Accordion
        display="flex"
        defaultIndex={[0]}
        index={accordionIndex}
        onChange={onAccordionChange}
        flexDirection="column"
        gap="3xs"
      >
        {!showSubOnly && (
          <AccordionItem border={0}>
            {({ isExpanded }) => (
              <Box
                borderWidth={1}
                borderRadius="base"
                borderColor={
                  isExpanded && isOptionsClickable
                    ? 'gray.600'
                    : 'blackAlpha.250'
                }
                _expanded={{ borderBottomWidth: 0, borderBottomRadius: 0 }}
              >
                <SingleVariantAccordionItem
                  title="One-time purchase"
                  variant={otpVariant}
                  isOptionsClickable={isOptionsClickable}
                  isExpanded={isExpanded}
                  availableForSale={availableForSale}
                  showSavingsAsAmount={showSavingsAsAmount}
                  isDiscountHidden={isDiscountHidden}
                  discountCodes={discountCodes}
                />
              </Box>
            )}
          </AccordionItem>
        )}

        <AccordionItem border={0}>
          {({ isExpanded }) => (
            <Box
              borderWidth={1}
              borderRadius="base"
              borderColor={
                isExpanded && isOptionsClickable ? 'gray.600' : 'blackAlpha.250'
              }
              _expanded={{ borderBottomWidth: 0, borderBottomRadius: 0 }}
            >
              <SingleVariantAccordionItem
                title="Subscribe & Save"
                variant={defaultSubscription}
                otpPrice={defaultSubscription.otpPrice}
                isOptionsClickable={isOptionsClickable}
                isExpanded={isExpanded}
                availableForSale={availableForSale}
                showSavingsAsAmount={showSavingsAsAmount}
                isDiscountHidden={isDiscountHidden}
                discountCodes={discountCodes}
              />
              <MotionAccordionPanel pt={0} pb="xs">
                {hasMoreThanOneSub && (
                  <Select
                    aria-label="Select a subscription plan"
                    value={
                      isSubscription(selectedVariant)
                        ? selectedVariant.sellingPlan.id
                        : defaultSubscription.sellingPlan.id
                    }
                    onChange={onSelectChange}
                    width={{ base: '100%', sm: 'auto' }}
                    flexShrink={0}
                    variant="rounded"
                    size="sm"
                    maxW={{ sm: 250 }}
                    options={subscriptions.map((sub) => {
                      const isDisabled =
                        !!('availableForSale' in sub) && !sub.availableForSale;
                      return {
                        value: sub.sellingPlan.id,
                        label: sub.sellingPlan.title,
                        isDisabled,
                      };
                    })}
                  />
                )}

                <Stack mt={hasMoreThanOneSub ? 'xs' : '5xs'} spacing="5xs">
                  {!hasMoreThanOneSub && isSubscription(selectedVariant) && (
                    <Flex alignItems="center" gap="3xs">
                      <Refresh boxSize="xs" />
                      <Text size="detail">
                        {defaultSubscription.sellingPlan.title}
                      </Text>
                    </Flex>
                  )}
                  <ProductConfiguratorCancellationNote />
                </Stack>
              </MotionAccordionPanel>
            </Box>
          )}
        </AccordionItem>
      </Accordion>
    </Wrapper>
  );
}
